import React, { useState } from 'react';

import { Button, Col, Row } from 'antd';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { IQuizAnswer, IQuizQuestion } from 'src/shared/models';
import { customFormatMessage, getTranslatedData } from 'utils';

interface IQuestion {
  saveAnswer: (idResourceAnswer: number) => void;
  handleSubmit: (idResourceAnswer: number) => void;
  showStatistics: boolean;
  allowContinue: boolean;
  totalResponses: number;
  questions: IQuizQuestion[];
}
export default function Question({
  saveAnswer,
  handleSubmit,
  allowContinue, // User can do the next question without select the correct answer
  questions,
  totalResponses,
  showStatistics,
}: IQuestion): JSX.Element {
  const { locale } = useIntl();
  const [userAnswer, setUserAnswer] = useState<null | IQuizAnswer>(null);
  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [showCorrect, setShowCorrect] = useState<boolean>(false);
  const [canContinue, setCanContinue] = useState<boolean>(false);
  const [answersSelectable, setAnswersSelectable] = useState<boolean>(true);
  const [question, setQuestion] = useState<IQuizQuestion>(questions[0]);
  const [questionNumber, setQuestionNumber] = useState<number>(0);
  const [responses, setResponses] = useState<number[]>([]);

  const statisticsColumnsSize = showStatistics && showCorrect ? 3 : 0;

  const handleConfirm = () => {
    const checkCorrection = answersSelectable;
    const goToNextQuestion = !question.isLastQuestion;
    const finalizeQuiz = !checkCorrection && question.isLastQuestion;
    switch (true) {
      case goToNextQuestion:
        saveAnswer(userAnswer.idResource);
        setUserAnswer(null);
        setShowAnswer(false);
        setAnswersSelectable(true);
        setShowCorrect(false);
        setCanContinue(false);
        setQuestionNumber(questionNumber + 1);
        setQuestion(questions[questionNumber + 1]);
        setResponses([]);
        break;
      case finalizeQuiz:
        handleSubmit(userAnswer.idResource);
        setUserAnswer(null);
        setCanContinue(false);
        break;
    }
  };

  const handleSelectAnswer = (_answer: IQuizAnswer) => {
    if (!answersSelectable) return;
    setResponses([...responses, _answer.idResource]);

    const selectCorrectAnswer_isRequired = false;
    const accept_one_response = true;
    switch (allowContinue) {
      case selectCorrectAnswer_isRequired:
        setShowAnswer(true);
        setShowCorrect(_answer.isCorrect);
        setAnswersSelectable(!_answer.isCorrect);
        setCanContinue(_answer.isCorrect);
        setUserAnswer(_answer.isCorrect ? _answer : null);
        break;

      case accept_one_response:
        setUserAnswer(_answer);
        setShowAnswer(true);
        setCanContinue(true);
        setAnswersSelectable(false);
        setShowCorrect(true);
        break;
    }
  };

  const renderQuestionButton = (): JSX.Element => {
    const label = question.isLastQuestion
      ? 'page.challenge.quiz.finalize'
      : 'app.custom-pagination.next';

    const icon = answersSelectable ? null : (
      <a className="icon icon__white icon--arrow-normal-right" />
    );

    return (
      <Row className="button-container" justify="end">
        <Col md={12 - statisticsColumnsSize} xs={24}>
          <Row justify="end">
            <Button
              block
              disabled={!canContinue}
              onClick={handleConfirm}
              className="btn-primary btn-primary--black button-container__content"
            >
              <Row justify="space-between" align="middle">
                <FormattedMessage id={label} />
                {icon}
              </Row>
            </Button>
          </Row>
        </Col>
        {showStatistics && <Col md={statisticsColumnsSize} xs={0} />}
      </Row>
    );
  };

  const renderVideo = (question: IQuizQuestion): JSX.Element => {
    const { urlVideo, isVideoFromFile } = question;

    if (isVideoFromFile) {
      return (
        <video
          className="question-media eventDetailPage__video"
          height="240"
          controls
        >
          <source src={urlVideo} type="video/mp4" />
        </video>
      );
    }
    return (
      <iframe
        allowFullScreen
        className="question-media eventDetailPage__video"
        style={{ minHeight: '380px' }}
        src={urlVideo}
      />
    );
  };

  const renderImage = (value: string): JSX.Element => (
    <img
      loading="lazy"
      className="question-media "
      src={value}
      alt="event_image"
    />
  );

  const renderChallengeMedia = (question.hasImage || question.hasVideo) && (
    <Row justify="center" align="middle">
      {question.hasVideo
        ? renderVideo(question)
        : renderImage(question.urlImage)}
    </Row>
  );

  const renderStatistics = (answer: IQuizAnswer): JSX.Element => {
    const { idResource, userResponses } = answer;
    const answerSelected = idResource == userAnswer?.idResource;

    const _usersResponses = userResponses + (answerSelected ? 1 : 0);
    const _totalResponses = totalResponses + (Boolean(userAnswer) ? 1 : 0);
    const _value = _usersResponses / _totalResponses || 0;

    return showStatistics && showCorrect ? (
      <Col
        className="question-statistics"
        md={statisticsColumnsSize}
        xs={2 + statisticsColumnsSize}
      >
        <Row justify="center" align="middle">
          <FormattedNumber
            value={_value}
            style="percent"
            currency={locale == 'es-ES' ? 'EUR' : 'USD'}
          />
        </Row>
      </Col>
    ) : null;
  };

  const renderAnswer = (answer: IQuizAnswer, key: number) => {
    const { idResource, isCorrect, name } = answer;
    const answerSelected = responses.includes(idResource);
    const selectableClass = answerSelected ? 'selected' : '';
    const incorrectAnswer = answerSelected ? 'incorrect' : '';
    const correctionClass =
      isCorrect && showCorrect ? 'correct' : incorrectAnswer;

    const answerClass = showAnswer ? correctionClass : selectableClass;
    const iconClass = !showAnswer ? 'check' : isCorrect ? 'check' : 'close';

    return (
      <Row
        className="question-answer-container"
        onClick={() => handleSelectAnswer(answer)}
        key={key}
      >
        <Col
          className={`question-answer question-answer--${answerClass}`}
          md={24 - statisticsColumnsSize}
          xs={(showStatistics && showCorrect ? 22 : 24) - statisticsColumnsSize}
        >
          <Row justify="space-between">
            <Col className="answer-title" xs={20} lg={22}>
              {getTranslatedData(answer, 'content') || name}
            </Col>
            <Col className={`answer answer--${answerClass}`} xs={4} lg={2}>
              <Row justify="center" align="middle">
                <a
                  className={`icon icon__increase_50 icon__white icon--${iconClass}`}
                />
              </Row>
            </Col>
          </Row>
        </Col>
        {renderStatistics(answer)}
      </Row>
    );
  };

  const renderQuestion = question?.answers?.map((answers, key: number) =>
    renderAnswer(answers, key)
  );

  const renderQuestions = (
    <Row justify="start">
      <Row align="middle">
        <Col md={24 - statisticsColumnsSize} xs={22 - statisticsColumnsSize}>
          <Row className="question-title">
            {getTranslatedData(question, 'content') || question.title}
          </Row>
        </Col>
        {showStatistics && showCorrect && (
          <Col
            className="question-title-statistics"
            md={statisticsColumnsSize}
            xs={statisticsColumnsSize + 2}
          >
            <FormattedMessage id="page.challenge.quiz.average-answer" />
          </Col>
        )}
      </Row>
      {renderQuestion}
      {showCorrect && question?.hasJustification && (
        <Col
          className="question-justification"
          md={24 - statisticsColumnsSize}
          xs={24}
        >
          <Row className="regular-body">
            {getTranslatedData(question, 'content') ||
              question.textJustification}
          </Row>
        </Col>
      )}
    </Row>
  );

  const renderQuestionCounter = (
    <Row className="question-counter" justify="start">
      <p>
        {customFormatMessage('page.challenge.quiz.question-{current}-{total}', {
          current: questionNumber + 1,
          total: questions.length,
        })}
      </p>
    </Row>
  );

  const renderTraining = (
    <Row justify="start" className="quiz-question-container">
      {renderChallengeMedia}
      {renderQuestionCounter}
      {renderQuestions}
      {renderQuestionButton()}
    </Row>
  );

  return renderTraining;
}
